import React, { useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SmokeReveal } from "./SmokeReveal";
import { ProductModal } from "./ProductModal";

const API = process.env.REACT_APP_BACKEND_URL;

const Card = ({ p, onView }) => {
  const stockVals = Object.values(p.stock || {});
  const soldOut = stockVals.length > 0 && stockVals.every((v) => Number(v) <= 0);
  return (
  <div
    className={`group relative overflow-hidden rounded-2xl border bg-surface transition-all duration-300 ${soldOut ? "border-white/5" : "border-white/10 hover:border-neon-pink/50"}`}
    data-testid={`merch-card-${p.id}`}
  >
    <div className="relative aspect-square overflow-hidden bg-black cursor-pointer" onClick={() => onView(p)}>
      <div className="absolute left-1/2 top-0 h-full w-2/3 -translate-x-1/2 bg-gradient-to-b from-neon-violet/25 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <img
        src={p.image}
        alt={p.name}
        className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 ${soldOut ? "opacity-40 saturate-50" : ""}`}
      />
      {p.badge && !soldOut && (
        <span className="absolute left-3 top-3 rounded-full bg-neon-pink px-3 py-1 font-mono-cap text-[9px] text-white">
          {p.badge}
        </span>
      )}
      {soldOut && (
        <span className="absolute left-3 top-3 rounded-full border border-neon-pink bg-void/90 px-3 py-1 font-mono-cap text-[9px] text-neon-pink" data-testid={`soldout-${p.id}`}>
          Sold Out
        </span>
      )}
      <button
        onClick={() => onView(p)}
        className="absolute inset-x-3 bottom-3 flex translate-y-0 items-center justify-center gap-2 rounded-full bg-white/10 py-3 font-mono-cap text-[10px] text-white backdrop-blur-md transition-transform duration-300 sm:translate-y-16 sm:group-hover:translate-y-0"
        data-testid={`merch-view-${p.id}`}
      >
        Quick View <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
    <div className="p-5">
      <p className="font-mono-cap text-[9px] text-neon-cyan">{p.variants}</p>
      <h3 className="mt-2 font-sub text-base font-bold text-white">{p.name}</h3>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-xl text-white">{p.price}</span>
        {p.was && <span className="font-body text-sm text-slate-500 line-through">{p.was}</span>}
        {soldOut && <span className="font-mono-cap text-[9px] text-neon-pink">Sold Out</span>}
      </div>
    </div>
  </div>
  );
};

export const Merch = () => {
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then((r) => r.json())
      .then((d) => setProducts((d.products || []).map((p) => ({ ...p, priceValue: p.price_value }))))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section id="merch" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32">
      <SmokeReveal tint="cyan" className="mb-14 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono-cap mb-4 text-[11px] text-neon-cyan">Official Merchandise</p>
          <h2 className="font-display text-4xl leading-[0.95] sm:text-6xl">
            WEAR THE <span className="gradient-text">WARRIOR</span>
          </h2>
        </div>
        <p className="max-w-sm font-body text-sm text-slate-400">
          Limited-edition event pieces from the Pahlawan fight nights. More drops landing soon — swap-ready as new
          products are released.
        </p>
      </SmokeReveal>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <Card p={p} onView={setActive} />
          </Reveal>
        ))}
      </div>

      {active && <ProductModal product={active} onClose={() => setActive(null)} />}
    </section>
  );
};
